import React from "react";
import { CustomerBaiscDetailsLayout } from "../../CustomerBasicDetailsLayout/CustomerBasicDetailsLayout";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { StandadCard } from "../../../../../components/Cards";
import { Box, useTheme } from "@mui/material";
import { PolicyTabCard } from "../../../../../components/PolicyTabCard/PolicyTabCard";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../hooks";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { PersonOutlineOutlined, Update } from "@mui/icons-material";
import { clearCustomerPolicyDetailsState } from "../../../../../redux/customerPolicyDetails/customerPolicyDetailsActions";

export const AutomobilePolicyLayout: React.FC = () => {
  const { customerId, customerPolicyId } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
  };
  // const [tab, setTab] = useState<number>(0);
  // The merged customer-policy-details fetch is dispatched by the CustomerInfo
  // screen via useCustomerPolicyDetails. The layout only observes that shared
  // slice for loading/error and the saved customer_policy_id used to enable the
  // sidebar tabs. It falls back to the route param so the tabs stay enabled
  // while viewing an existing policy (including direct navigation to non-basic
  // -details routes).
  const {
    customerPolicyId: mergedCustomerPolicyId,
    details: { loading, error },
  } = useSelector(
    (storeState: IStoreState) => storeState.customerPolicyDetails,
  );
  // Prefer the URL id as the source of truth. Only fall back to the merged
  // slice id when the URL has none (e.g. right after a new policy is saved in
  // this session). Preferring the slice id here caused a cross-customer leak:
  // when switching Customer A -> B, the slice still held A's id/details while
  // B's screen mounted, so tabs and the Outlet context resolved to A.
  const savedCustomerPolicyId = customerPolicyId ?? mergedCustomerPolicyId;
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const location = useLocation();
  const currentPathname = location.pathname;
  const theme = useTheme();
  const isMainTab = currentPathname.includes("basic-details");

  // Tracks whether the customer-policy-details slice has finished loading at
  // least once for the CURRENT customer/policy. We only show the full-layout
  // loader on that first load. Later background refetches (e.g. when a child
  // panel such as the Vehicle "Add" form mounts and calls
  // useCustomerPolicyDetails) flip the shared slice back to InProgress and even
  // wipe its data; without this guard the layout PageLoader would blank the
  // Outlet, unmount/remount the routed form, and re-fire its data fetches
  // (the "page reloads / needs two clicks" bug).
  const hasLoadedOnceRef = React.useRef(false);
  React.useEffect(() => {
    // Reset when the customer/policy in the URL changes.
    hasLoadedOnceRef.current = false;
  }, [customerId, customerPolicyId]);
  if (loading === LoadState.Loaded) {
    hasLoadedOnceRef.current = true;
  }

  const handleStep = (step: string) => () => {
    const isAlreadyInSamePage = currentPathname.includes(step);
    if (!isAlreadyInSamePage) {
      navigate(
        `/automobile-policy/${customerId}/${step}/${savedCustomerPolicyId}?tab=0`
      );
    }
  };

  const renderComponent = () => {
    if (isMainTab) {
      return <Outlet />;
    } else if (!isMainTab && !customerPolicyId) {
      return <Navigate to={`/customer-overview/${customerId}`} />;
    } else if (!isMainTab && savedCustomerPolicyId) {
      return <Outlet context={[customerId, savedCustomerPolicyId]} />;
    } else {
      console.log("__FALIED____");
      return null;
    }
  };
  // const handleChangetab = (event: React.SyntheticEvent, newValue: number) => {
  //   setTab(newValue);
  // };

  // Clear the merged customer-policy-details slice whenever the customer or
  // policy in the URL CHANGES. React Router keeps this layout mounted while
  // only the :customerId/:customerPolicyId params change (same route pattern),
  // so the unmount cleanup below never fires when navigating A -> B -> A.
  // Without this reset, the previously opened customer's details stay in the
  // slice and leak into the next customer's form until the re-fetch resolves.
  //
  // The first run is skipped: on the initial mount the child form (CustomerInfo
  // via useCustomerPolicyDetails) has already dispatched its fetch (effects run
  // child-first, then parent), so clearing here would wipe that in-flight load.
  // We only need to clear on subsequent customer/policy transitions.
  const hasMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    dispatch(clearCustomerPolicyDetailsState());
    dispatch({ type: "CLEAR_POLICY_STATE" });
  }, [customerId, customerPolicyId]);

  React.useEffect(() => {
    return () => {
      dispatch(clearCustomerPolicyDetailsState());
      dispatch({ type: "CLEAR_POLICY_STATE" });
    };
  }, []);

  // const tabStyle = {
  //   fontWeight: 800,
  //   color: "Black"
  // };

  return (
    <>
      <CustomerBaiscDetailsLayout
        customerId={customerId}
        fromPolicy
        policyId={savedCustomerPolicyId as number}
      >
          <Box>
            <StandadCard sx={{ mb: 1 }}>
              <Box display={"flex"} flexWrap={"wrap"}>
                <PolicyTabCard
                  label="Basic Details"
                  icon={PersonOutlineOutlined}
                  onClick={handleStep("basic-details")}
                  isSelected={currentPathname.includes("basic-details")}
                />

                <PolicyTabCard
                  label="Drivers"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("driver-tab")}
                  isSelected={currentPathname.includes("driver-tab")}
                />

                <PolicyTabCard
                  label="Vehicles"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("vehicle-tab")}
                  isSelected={currentPathname.includes("vehicle-tab")}
                />

                <PolicyTabCard
                  label="Coverage"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("vehicle-coverage-schedule-tab")}
                  isSelected={currentPathname.includes(
                    "vehicle-coverage-schedule-tab"
                  )}
                />
                <PolicyTabCard
                  label="Applications"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("automobile-application-tab")}
                  isSelected={currentPathname.includes(
                    "automobile-application-tab"
                  )}
                />
                <PolicyTabCard
                  label="Attachments"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("attachment-tab")}
                  isSelected={currentPathname.includes("attachment-tab")}
                />
                 <PolicyTabCard
                  label="Premium Finance"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("premium-finance")}
                  isSelected={currentPathname.includes("premium-finance")}
                />
                <PolicyTabCard
                  label="eDocs"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("csio-edocs-list")}
                  isSelected={currentPathname.includes("csio-edocs-list")}
                />
                {/* <PolicyTabCard
                label="Alberta Applications"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("automobile-alberta-application-tab")}
                isSelected={currentPathname.includes(
                  "automobile-alberta-application-tab"
                )}
              /> */}
                <PolicyTabCard
                  label="Policy Forms"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("policy-forms-tab")}
                  isSelected={currentPathname.includes("policy-forms-tab")}
                />
                <PolicyTabCard
                  label="Quote"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("quote")}
                  isSelected={currentPathname.includes("quote")}
                />
                <PolicyTabCard
                  label="Telematics"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("telematics")}
                  isSelected={currentPathname.includes("telematics")}
                />
                <PolicyTabCard
                  label="G4 Sign"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("g4-sign")}
                  isSelected={currentPathname.includes("g4-sign")}
                />
                <PolicyTabCard
                  label="My Proof of Insurance"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("csio-mpoi")}
                  isSelected={currentPathname.includes("csio-mpoi")}
                />
                <PolicyTabCard
                  label="Updates"
                  icon={Update}
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("updates")}
                  isSelected={currentPathname.includes("updates")}
                />
                <PolicyTabCard
                  label="Invoices"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("invoices")}
                  isSelected={currentPathname.includes("invoices")}
                />
                {/* <PolicyTabCard
                label="History"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("history-tab")}
                isSelected={currentPathname.includes("history-tab")}
              /> */}
                
                {/* <PolicyTabCard
                label="Abeyance/Notes"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("notes-tab")}
                isSelected={currentPathname.includes("notes-tab")}
              /> */}
                {/* <PolicyTabCard
                label="Policy Notes"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("policy-notes-tab")}
                isSelected={currentPathname.includes("policy-notes-tab")}
              /> */}

                

                {/* <PolicyTabCard
                label="eDocs/EDI"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("csio-edi-list")}
                isSelected={currentPathname.includes("csio-edi-list")}
              /> */}
                <PolicyTabCard
                  label="Policy Changes/Upload"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("policy-change-request-list")}
                  isSelected={currentPathname.includes(
                    "policy-change-request-list"
                  )}
                />
                <PolicyTabCard
                  label="Contacts"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("contacts")}
                  isSelected={currentPathname.includes("contacts")}
                />
                <PolicyTabCard
                  label="Activities"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("all-tasks")}
                  isSelected={currentPathname.includes("all-tasks")}
                />
                <PolicyTabCard
                  label="Email Thread"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("email-thread")}
                  isSelected={currentPathname.includes("email-thread")}
                />
                <PolicyTabCard
                  label="Sequences"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("sequences")}
                  isSelected={currentPathname.includes("sequences")}
                />
                  <PolicyTabCard
                  label="Certificate of Insurance"
                  disabled={!savedCustomerPolicyId}
                  onClick={handleStep("certificate-of-insurance")}
                  isSelected={currentPathname.includes("certificate-of-insurance")}
                />
               
                
              
                
              </Box>
            </StandadCard>
          </Box>

          <PageLoader
            // Only show the full-layout loader on the INITIAL load (before any
            // policy details exist). Background refetches of the shared
            // customerPolicyDetails slice (e.g. triggered when a child panel
            // like the Vehicle form mounts and calls useCustomerPolicyDetails)
            // must NOT blank the Outlet — doing so unmounted and remounted the
            // routed form, re-firing its data fetches and making record panels
            // (Vehicle/Driver "Add") reload the page / need two clicks.
            loading={
              loading === LoadState.InProgress && !hasLoadedOnceRef.current
            }
            error={error ? { message: error } : null}
          >
            {renderComponent()}
          </PageLoader>
        </CustomerBaiscDetailsLayout>
    </>
  );
};
